import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X, Paperclip, FileDown, Edit2 } from 'lucide-react';
import Shell from '../../components/TopBarShell';
import StatusBadge from '../../components/StatusBadge';
import OdooBadge from '../../components/OdooBadge';
import { Button } from '../../components/ui/button';
import { useReviewQueue } from '../../contexts/ReviewQueueContext';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import { useAuth } from '../../contexts/AuthProvider';
import api from '../../utils/api';

const fmtMoney = (n) => `€ ${Number(n || 0).toFixed(2)}`;

const ReviewDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { count: reviewCount, refresh } = useReviewQueue();
  const t = useT();
  const { intlLocale } = useLocale();
  const { user } = useAuth();
  const fmtDate = (iso) => iso ? new Intl.DateTimeFormat(intlLocale).format(new Date(iso)) : '·';

  const [report, setReport] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    api.adminMe().then(d => setIsAdmin(!!d?.is_admin)).catch(() => setIsAdmin(false));
  }, []);

  const downloadCombinedPdf = async () => {
    if (!report) return;
    setDownloadingPdf(true);
    setError('');
    try {
      const blob = await api.combinedPdf(report.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NDF-${String(report.id).padStart(5, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('editor.pdfFailed'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    api.expense(id).then(setReport).catch(() => setError(t('editor.notFound')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const decide = async (action) => {
    setBusy(true);
    setError('');
    try {
      const fn = action === 'approve' ? api.approveExpense : api.rejectExpense;
      const r = await fn(id, comment.trim());
      setReport(r);
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || t('review.actionImpossible'));
    } finally {
      setBusy(false);
    }
  };

  if (!report) {
    return (
      <Shell reviewCount={reviewCount}>
        <div className="text-sm text-[hsl(var(--text-soft))]">{error || t('common.loading')}</div>
      </Shell>
    );
  }

  const totalHtva = report.lines.reduce((s, l) => s + Number(l.total_htva || 0), 0);
  const totalTvac = report.lines.reduce((s, l) => s + Number(l.total_tvac || 0), 0);
  const isPending = report.status === 'submitted';

  return (
    <Shell reviewCount={reviewCount}>
      <button onClick={() => navigate('/review')} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))] mb-3">
        <ArrowLeft className="h-4 w-4" /> {t('review.title')}
      </button>

      <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[13px] text-[hsl(var(--text-muted))] font-num">NDF-{String(report.id).padStart(5, '0')}</div>
          <h1 className="font-display text-[1.75rem] mt-1">{report.title || t('expense.noteOf', { date: fmtDate(report.created_at) })}</h1>
          <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">
            {t('review.submittedBy')} <span className="text-[hsl(var(--text))]">{report.submitter_full_name || report.submitter_email}</span>
            &nbsp;· <span className="font-num text-[13px]">{report.submitter_email}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Manager + admin can tweak title / dates / line copy on
              still-open notes. Hidden once accounting has been notified
              (approved) because the PDF is already on its way. */}
          {report.status !== 'approved'
            && (isAdmin
                || (report.submitter_manager_email || '').toLowerCase() === (user?.email || '').toLowerCase()) && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/expenses/${report.id}`)} className="gap-1.5">
              <Edit2 className="h-3.5 w-3.5" /> {t('review.editNote') || 'Modifier'}
            </Button>
          )}
          <OdooBadge odoo={report.odoo} />
          <StatusBadge status={report.status} />
        </div>
      </header>

      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}

      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Static label={t('editor.department')} value={report.submitter_departement || '·'} />
        <Static label={t('review.submittedOn')} value={fmtDate(report.submitted_at)} />
        <Static label={t('review.totalHtva')} value={fmtMoney(totalHtva)} mono />
        <Static label={t('review.totalTvac')} value={fmtMoney(totalTvac)} mono emphasize />
      </section>

      {report.note && (
        <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
          <div className="text-[13px] text-[hsl(var(--text-muted))] mb-1.5">{t('review.collaboratorNote')}</div>
          <div className="text-[15px]">{report.note}</div>
        </section>
      )}

      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-border">
          <h2 className="text-[15px] font-semibold">{t('editor.lines')}</h2>
        </div>
        <table className="w-full text-[15px]">
          <thead className="bg-[hsl(var(--bg-subtle))] text-[13px] text-[hsl(var(--text-muted))]">
            <tr>
              <th className="text-left font-medium px-4 py-3 w-[120px]">{t('expense.col.date')}</th>
              <th className="text-left font-medium px-4 py-3 w-[130px]">{t('editor.col.type')}</th>
              <th className="text-left font-medium px-4 py-3">{t('editor.col.description')}</th>
              <th className="text-left font-medium px-4 py-3">{t('editor.col.participants')}</th>
              <th className="text-right font-medium px-4 py-3 w-[120px]">{t('editor.col.htva')}</th>
              <th className="text-right font-medium px-4 py-3 w-[120px]">{t('editor.col.tvac')}</th>
            </tr>
          </thead>
          <tbody>
            {report.lines.map(l => (
              <tr key={l.id} className="border-t border-[hsl(var(--rule))]">
                <td className="px-4 py-3 font-num text-[13px]">{fmtDate(l.date)}</td>
                <td className="px-4 py-3">{t(`expense.type.${l.type}`)}</td>
                <td className="px-4 py-3">{l.description || '·'}</td>
                <td className="px-4 py-3 text-[hsl(var(--text-soft))]">{l.participants || '·'}</td>
                <td className="px-4 py-3 text-right font-num">{fmtMoney(l.total_htva)}</td>
                <td className="px-4 py-3 text-right font-num">{fmtMoney(l.total_tvac)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-[hsl(var(--bg-subtle))]">
              <td colSpan={4} className="px-4 py-3.5 text-[15px] font-semibold">{t('editor.totals')}</td>
              <td className="px-4 py-3.5 text-right font-num">{fmtMoney(totalHtva)}</td>
              <td className="px-4 py-3.5 text-right font-num font-semibold">{fmtMoney(totalTvac)}</td>
            </tr>
          </tfoot>
        </table>
      </section>

      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-3">{t('editor.attachments')}</h2>
        {report.attachments.length === 0
          ? <p className="text-sm text-[hsl(var(--text-soft))]">{t('editor.noAttachments')}</p>
          : (
            <>
              <ul className="space-y-1.5">
                {report.attachments.map(a => {
                  const idx = report.lines.findIndex(l => l.id === a.line);
                  const line = idx >= 0 ? report.lines[idx] : null;
                  const lineLabel = line
                    ? t('editor.attach.lineOption', {
                        n: idx + 1,
                        date: line.date
                          ? new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: '2-digit' }).format(new Date(line.date))
                          : '·',
                        desc: line.description?.trim() || t(`expense.type.${line.type}`),
                      })
                    : t('editor.attach.line.none');
                  return (
                    <li key={a.id} className="flex items-center gap-3 px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-sm">
                      <a href={a.file} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[hsl(var(--accent))] hover:underline truncate flex-1 min-w-0">
                        <Paperclip className="h-4 w-4 shrink-0" />
                        <span className="truncate">{a.original_name || a.file.split('/').pop()}</span>
                      </a>
                      <span className={`text-[12px] truncate shrink-0 max-w-[55%] ${line ? 'text-[hsl(var(--text-soft))]' : 'text-[hsl(var(--text-muted))] italic'}`}>
                        {lineLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={downloadCombinedPdf}
                  disabled={downloadingPdf} className="gap-1.5">
                  <FileDown className="h-3.5 w-3.5" />
                  {downloadingPdf ? t('editor.pdfBuilding') : t('editor.downloadCombined')}
                </Button>
              </div>
            </>
          )
        }
      </section>

      {isPending ? (
        <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5">
          <h2 className="text-[15px] font-semibold mb-3">{t('review.decision')}</h2>
          <div className="field mb-4">
            <label className="field-label">
              {t('review.comment')} <span className="text-[hsl(var(--text-muted))]">({t('review.commentHint')})</span>
            </label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
              placeholder={t('review.comment.ph')}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => decide('reject')} className="gap-2">
              <X className="h-4 w-4" /> {t('review.reject')}
            </Button>
            <Button disabled={busy} onClick={() => decide('approve')} className="gap-2">
              <Check className="h-4 w-4" /> {t('review.approve')}
            </Button>
          </div>
        </section>
      ) : (
        <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5">
          <h2 className="text-[15px] font-semibold mb-2">{t('review.decision')}</h2>
          <div className="text-sm text-[hsl(var(--text-soft))]">
            {t('editor.decidedBy', {
              decision: report.status === 'approved' ? t('status.approved') : t('status.rejected'),
              who: report.manager_decided_by_email,
              date: fmtDate(report.manager_decided_at),
            })}
          </div>
          {report.manager_comment && (
            <div className="mt-2 border-l-2 border-[hsl(var(--rule-strong))] pl-3 py-1 text-sm">
              {report.manager_comment}
            </div>
          )}
        </section>
      )}
    </Shell>
  );
};

const Static = ({ label, value, mono, emphasize }) => (
  <div>
    <div className="text-[13px] text-[hsl(var(--text-muted))]">{label}</div>
    <div className={`mt-1 ${mono ? 'font-num' : ''} ${emphasize ? 'text-[17px] font-semibold' : 'text-[15px]'}`}>{value}</div>
  </div>
);

export default ReviewDetail;
